import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Chip,
  Button,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  useTheme,
  useMediaQuery,
  styled,
  GlobalStyles,
  Slide,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HistoryIcon from "@mui/icons-material/History";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";

import { useDispatch, useSelector } from "react-redux";
import { logoutUserAsync } from "../../redux/usersSlice";
import { fetchProfilesOfCurrentUser } from "../../redux/profilesSlice";
import { getUserProfilePicture } from "../../services/apiService";
import { useNavigate } from "react-router-dom";

import UsersManagement from "../users/UsersManagement";
import ProfileList from "../Profiles/ProfileList";
import Statistics from "../Statistics/Statistics";
import Profile from "../users/Profile";
import Chatbot from "../Chatbot/Chatbot";
import AddProfileForm from "../Profiles/AddProfileForm";
import Historique from "../Historique/Historique";
import SearchManagement from "../SearchManagement/SearchManagement";
import MyProfiles from "../Profiles/MyProfiles";
import AppelOffreManagement from "../AppelOffre/AppelOffreManagement";
import AppelOffrePublie from "../AppelOffre/AppelOffrePublie";
import ProfilCandidature from "../Profiles/ProfilCandidature";
import CandidaturesManagement from "../Candidatures/CandidaturesManagement";
import MyCandidatures from "../Candidatures/MyCandidatures";
import ConfirmationDialog from "../Common/ConfirmationQuitFormModal";
import PlansModal from "./PlansModal";

import {
  getMedusaInspiredColor,
  getLabelForRole,
  getColorForRole,
} from "../../utils/utils";

// Style tokens
const SIDEBAR_WIDTH = 300;      // expanded width (unchanged)
const COLLAPSED_WIDTH = 80;     // collapsed (icons only)
const sidebarGradient =
  "linear-gradient(180deg,#2F4E8B 0%,#27437f 40%,#5e8fd3 100%)";
const sidebarHover = "rgba(255,255,255,0.12)";
const darkText = "#16213E";

const getInitials = (name = "User") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

// Styled drawer & nav item
const MiniDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ collapsed }) => ({
  width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
    background: sidebarGradient, // Ensure blue gradient
    color: "#fff",
    borderRight: "none",
    overflowX: "hidden",
    transition: "width 200ms ease",
  },
}));

const NavItem = ({ icon, label, selected, onClick, collapsed }) => (
  <ListItemButton
    onClick={onClick}
    disableRipple
    sx={{
      my: 0.5,
      pl: collapsed ? 1.5 : 3,
      pr: collapsed ? 1.5 : 3,
      height: { xs: 40, sm: 48 },
      justifyContent: collapsed ? "center" : "flex-start",
      ...(selected && {
        backgroundColor: "#ffffff",
        color: darkText,
        borderTopLeftRadius: 24,
        borderBottomLeftRadius: 24,
        marginRight: "-8px",
        boxShadow: "2px 0 6px rgba(0,0,0,0.08)",
      }),
      "&:hover": {
        backgroundColor: selected ? "#ffffff" : sidebarHover,
      },
    }}
  >
    <ListItemIcon
      sx={{
        color: selected ? darkText : "#ffffff",
        minWidth: { xs: 30, sm: 40 },
        justifyContent: "center",
      }}
    >
      {icon}
    </ListItemIcon>
    {!collapsed && (
      <ListItemText
        primary={label}
        sx={{ whiteSpace: "nowrap" }}
        primaryTypographyProps={{
          variant: "body2",
          fontWeight: 700,
          letterSpacing: "0.04em",
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
        }}
      />
    )}
  </ListItemButton>
);

// Dashboard
export default function Dashboard() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState("profile");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingComp, setPendingComp] = useState(null);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const addProfileFormRef = useRef(null);

  // Selectors
  const currentUser = useSelector((s) => s.users.currentUser);
  const { currentUserProfiles = [] } = useSelector((s) => s.profiles);
  const hasCandidateProfile = currentUserProfiles.length > 0;

  // Data fetching & listeners
  useEffect(() => {
    if (currentUser?.roles.includes("ROLE_USER"))
      dispatch(fetchProfilesOfCurrentUser());
    if (currentUser?.profilePicture)
      getUserProfilePicture(currentUser.profilePicture).then(setProfilePic);
  }, [dispatch, currentUser]);

  useEffect(() => {
    const onNav = (e) => {
      const { component } = e.detail;
      if (component === "seeProfileCondidature") {
        setSelectedComponent(
          currentUser?.roles.includes("ROLE_USER")
            ? "profilCandidature"
            : "myProfiles"
        );
      } else {
        setSelectedComponent(component);
      }
    };
    window.addEventListener("navigateToComponent", onNav);
    return () => window.removeEventListener("navigateToComponent", onNav);
  }, [currentUser]);

  useEffect(() => {
    const toProfile = () => setSelectedComponent("profile");
    window.addEventListener("redirectToMyProfile", toProfile);
    return () => window.removeEventListener("redirectToMyProfile", toProfile);
  }, []);

  // Handlers
  const toggleDrawer = () => setCollapsed((c) => !c);
  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleLogout = () => {
    handleMenuClose();
    dispatch(logoutUserAsync());
  };

  const handleComponentChange = (component) => {
    if (
      selectedComponent === "addprofile" &&
      addProfileFormRef.current?.isFormDirty()
    ) {
      setIsDialogOpen(true);
      setPendingComp(component);
    } else {
      setSelectedComponent(component);
    }
    if (isMobile) setMobileOpen(false);
  };

  // Navigation items
  const navItems = [
    {
      key: "profiles",
      label: "Chercher des profils",
      icon: <SearchIcon />,
      show: ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_COLLABORATOR"],
    },
    {
      key: "addprofile",
      label: "Ajouter profil",
      icon: <AddCircleIcon />,
      show: currentUser?.roles.includes("ROLE_USER")
        ? (!hasCandidateProfile ? ["ROLE_USER"] : [])
        : ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_COLLABORATOR"],
    },
    {
      key: currentUser?.roles.includes("ROLE_USER")
        ? "profilCandidature"
        : "myProfiles",
      label: currentUser?.roles.includes("ROLE_USER")
        ? "Profil de Candidature"
        : "Mes Profils",
      icon: <AccountBoxIcon />,
    },
    { key: "statistics", label: "Statistiques", icon: <BarChartIcon />, show: ["ROLE_SUPER_ADMIN"] },
    {
      key: "historique",
      label: "Historique",
      icon: <HistoryIcon />,
      show: ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_COLLABORATOR"],
    },
    {
      key: "appelOffre",
      label: "Appels d'offre",
      icon: <RequestQuoteIcon />,
      show: ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_COLLABORATOR"],
    },
    { key: "appelOffrePublie", label: "Offres Publiées", icon: <BusinessCenterIcon /> },
    {
      key: "candidaturesManagement",
      label: "Candidatures",
      icon: <ListAltIcon />,
      show: ["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_COLLABORATOR"],
    },
    {
      key: "myCandidatures",
      label: "Mes Candidatures",
      icon: <AssignmentIndIcon />,
      show: ["ROLE_USER"],
    },
  ].filter(
    (it) =>
      (!it.show || it.show.some((r) => currentUser?.roles.includes(r)))
  );

  // Drawer content
  const drawer = (
    <Box height="100%" display="flex" flexDirection="column">
      {/* top brand & toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          p: { xs: 1, sm: 2 },
        }}
      >
        {!collapsed && (
          <Typography
            variant="h6"
            fontWeight={700}
            color="#fff"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Tallentum
          </Typography>
        )}
        <IconButton onClick={toggleDrawer} sx={{ color: "#fff" }}>
          <MenuIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mx: collapsed ? 1 : 3 }} />

      <List sx={{ flex: 1 }}>
        {navItems.map((it) => (
          <NavItem
            key={it.key}
            icon={it.icon}
            label={it.label}
            selected={selectedComponent === it.key}
            onClick={() => handleComponentChange(it.key)}
            collapsed={collapsed}
          />
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mx: collapsed ? 1 : 3 }} />

      {/* bottom user box – back to horizontal layout when expanded */}
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: collapsed ? 0 : 1.5,
          flexDirection: collapsed ? "column" : "row",
        }}
      >
        <Avatar
          src={profilePic || undefined}
          sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            bgcolor: profilePic ? undefined : getMedusaInspiredColor(currentUser?.username),
          }}
        >
          {!profilePic && getInitials(currentUser?.username)}
        </Avatar>

        {!collapsed && (
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              {currentUser?.username}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: { xs: "0.625rem", sm: "0.75rem" } }}
            >
              {currentUser?.email}
            </Typography>
          </Box>
        )}

        {!collapsed && <Box flexGrow={1} />}
      </Box>
    </Box>
  );

  // Header bar
  const Header = () => (
    <Typography
      variant="h4"
      fontWeight={600}
      color={darkText}
      sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" } }}
    >
      <span style={{ color: "#27437f" }}>Bienvenue</span>,{" "}
      <span style={{ color: "#27437f" }}>
        {currentUser?.username || "User"}
      </span>{" "}
      <Chip
        label={getLabelForRole(currentUser?.roles[0]) || "User"}
        size="small"
        sx={{
          ml: 1,
          bgcolor: getColorForRole(currentUser?.roles[0]) || "#0060ff",
          color: "#27437f",
          fontSize: { xs: "0.625rem", sm: "0.75rem" },
        }}
      />
    </Typography>
  );

  // Component router
  const renderComponent = () => {
    switch (selectedComponent) {
      case "users":
        return <UsersManagement />;
      case "profiles":
        return <ProfileList />;
      case "statistics":
        return <Statistics />;
      case "addprofile":
        return <AddProfileForm ref={addProfileFormRef} />;
      case "historique":
        return <Historique />;
      case "searchManagement":
        return <SearchManagement />;
      case "myProfiles":
        return <MyProfiles />;
      case "profilCandidature":
        return <ProfilCandidature />;
      case "appelOffre":
        return <AppelOffreManagement />;
      case "appelOffrePublie":
        return <AppelOffrePublie />;
      case "candidaturesManagement":
        return <CandidaturesManagement />;
      case "myCandidatures":
        return <MyCandidatures />;
      default:
        return <Profile />;
    }
  };

  // Layout
  const drawerWidth = collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />

      {/* Permanent drawer on desktop */}
      {!isMobile && (
        <MiniDrawer variant="permanent" collapsed={collapsed}>
          {drawer}
        </MiniDrawer>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: SIDEBAR_WIDTH,
              background: sidebarGradient, // Explicitly set blue gradient
              color: "#fff",
              borderRight: "none",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Fixed white pill panel */}
      <Box
        component="main"
        className="main-content"
        sx={{
          position: "fixed",
          top: 0,
          left: { xs: 0, sm: `calc(${drawerWidth}px - 35px)` },
          right: 0,
          bottom: 0,
          overflow: "hidden",
          p: { xs: 1, sm: 2, md: 4 },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: 3,
            bgcolor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: { xs: 1, sm: 2 },
              borderBottom: "1px solid #eee",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
              {isMobile && (
                <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                  <MenuIcon sx={{ color: "#253c7f" }} />
                </IconButton>
              )}
              <Header />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => setIsPlansModalOpen(true)}
                sx={{
                  textTransform: "none",
                  bgcolor: "#f59e0b",
                  "&:hover": { bgcolor: "#d97706" },
                  fontWeight: "bold",
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 1.5, sm: 3 },
                  borderRadius: "50px",
                  color: "#fff",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Devenir Premium
              </Button>
       {/*        <Button
                variant="outlined"
                onClick={() => navigate("/landingdashboard")}
                sx={{
                  textTransform: "none",
                  borderColor: "#2F4E8B",
                  color: "#2F4E8B",
                  fontWeight: "bold",
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 1.5, sm: 3 },
                  borderRadius: "50px",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  ml: 1,
                  "&:hover": {
                    borderColor: "#27437f",
                    background: "#f3f6fa",
                  },
                }}
              >
                Landing
              </Button> */}
              <Tooltip title="Notifications">
                <IconButton>
                  <NotificationsIcon sx={{ color: "#253c7f", fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Paramètres">
                <IconButton onClick={handleMenuOpen}>
                  <SettingsIcon sx={{ color: "#253c7f", fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>

              {/* Paramètres menu */}
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                keepMounted
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setSelectedComponent("profile");
                  }}
                >
                  <MuiListItemIcon>
                    <AccountBoxIcon fontSize="small" sx={{ color: "#253c7f" }} />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Visiter profil"
                    primaryTypographyProps={{
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      color: "#253c7f",
                    }}
                  />
                </MenuItem>

                {(currentUser?.roles.includes("ROLE_SUPER_ADMIN") ||
                  currentUser?.roles.includes("ROLE_ADMIN")) && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      setSelectedComponent("users");
                    }}
                  >
                    <MuiListItemIcon>
                      <PeopleOutlineIcon fontSize="small" sx={{ color: "#253c7f" }} />
                    </MuiListItemIcon>
                    <MuiListItemText
                      primary="Utilisateurs"
                      primaryTypographyProps={{
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        color: "#253c7f",
                      }}
                    />
                  </MenuItem>
                )}

                <MenuItem onClick={handleLogout}>
                  <MuiListItemIcon>
                    <ExitToAppIcon fontSize="small" sx={{ color: "#253c7f" }} />
                  </MuiListItemIcon>
                  <MuiListItemText
                    primary="Déconnexion"
                    primaryTypographyProps={{
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      color: "#253c7f",
                    }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Page content */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: { xs: 1, sm: 2 },
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-track": { background: "rgba(255,255,255,0.15)" },
              "&::-webkit-scrollbar-thumb": { background: "#27437f", borderRadius: 4 },
            }}
          >
            <Slide in direction="up" mountOnEnter unmountOnExit>
              <Box>
                {renderComponent()}
                <Box sx={{ mt: 2 }}>
                  <Chatbot />
                </Box>
              </Box>
            </Slide>
          </Box>
        </Paper>
      </Box>

      {/* Background + modals */}
      <GlobalStyles styles={{ body: { margin: 0, overflow: "hidden", background: sidebarGradient } }} />

      <ConfirmationDialog
        open={isDialogOpen}
        title="Confirmer la navigation"
        message="Êtes-vous sûr de vouloir quitter le formulaire ? Les modifications non enregistrées seront perdues."
        onClose={(confirm) => {
          setIsDialogOpen(false);
          if (confirm && pendingComp) {
            addProfileFormRef.current?.setIsFormDirty(false);
            setSelectedComponent(pendingComp);
            setPendingComp(null);
          }
        }}
      />

      <PlansModal open={isPlansModalOpen} onClose={() => setIsPlansModalOpen(false)} />
    </Box>
  );
}